import { useEffect, useState, useMemo } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { Printer, RefreshCcw, ArrowUpRight, ArrowDownRight, Wallet, Receipt, Ticket, Home } from "lucide-react";

const METHOD_LABEL = {
  cash: "Cash", upi: "UPI", card: "Card", stripe: "Stripe", bank: "Bank Transfer",
  unspecified: "Unspecified",
};

export default function AdminDayClose() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/accounting/day-close", { params: { date } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [date]);  // eslint-disable-line react-hooks/exhaustive-deps

  const methods = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.receipts_by_method || {}).sort((a, b) => b[1] - a[1]);
  }, [data]);

  const print = () => window.print();

  return (
    <div data-testid="admin-day-close" className="print:bg-white print:text-black">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8 print:hidden">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Front Desk · End of Day</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none">Day Close.</h1>
          <p className="text-sm text-white/60 mt-2 max-w-2xl">
            Reconcile today&apos;s bookings, paid invoices and cash expenses. The <span className="brand-orange">Net cash in hand</span> figure below tells you exactly how much physical cash should be in the till before handover.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            data-testid="dayclose-date"
            className="bg-[#131A2B] border border-white/12 py-2 px-3 text-white text-sm" />
          <button onClick={load} data-testid="dayclose-refresh" className="p-2 border border-white/20 hover:border-brand-blue hover:text-brand-blue">
            <RefreshCcw size={14} />
          </button>
          <button onClick={print} data-testid="dayclose-print" className="bg-brand-blue text-white px-4 py-2 text-xs uppercase font-bold tracking-widest flex items-center gap-2 hover:glow-blue">
            <Printer size={13} /> Print / PDF
          </button>
        </div>
      </div>

      {/* Print header (visible in print only) */}
      <div className="hidden print:block mb-6">
        <h1 className="text-2xl font-bold">HTP Sports Hub · Day Close Report</h1>
        <div className="text-sm text-neutral-600">Date: {date} · Registered NGO · GST-exempt</div>
      </div>

      {loading && <div className="text-white/40 font-mono uppercase text-xs">Loading…</div>}

      {data && (
        <>
          {/* Headline KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Kpi icon={ArrowUpRight} tone="blue"  label="Gross Receipts" value={data.totals.gross_receipts} testid="kpi-gross" />
            <Kpi icon={ArrowDownRight} tone="red" label="Expenses"       value={data.totals.expenses_amount} testid="kpi-exp" />
            <Kpi icon={Receipt}   tone="orange"   label="Net Receipts"   value={data.totals.net_receipts} testid="kpi-net" />
            <Kpi icon={Wallet}    tone="lime"     label="Cash in Hand"   value={data.cash.net_cash_in_hand} testid="kpi-cash" bold />
          </div>

          {/* Payment method breakdown + Category breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
            <div className="border border-white/12 bg-[#131A2B] p-5 print:border-neutral-300 print:bg-white">
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3 print:text-neutral-600">Receipts by payment method</div>
              {methods.length === 0 ? <div className="text-white/40 text-sm">No receipts today.</div> :
                <table className="w-full text-sm" data-testid="method-table">
                  <tbody>
                    {methods.map(([m, v]) => (
                      <tr key={m} className="border-b border-white/8 last:border-0 print:border-neutral-200">
                        <td className="py-2 font-mono uppercase tracking-widest text-white/70 print:text-black">{METHOD_LABEL[m] || m}</td>
                        <td className="py-2 text-right font-display font-bold brand-blue print:text-black" data-testid={`method-${m}`}>{formatINR(v)}</td>
                      </tr>
                    ))}
                    <tr className="font-bold">
                      <td className="pt-3">Total</td>
                      <td className="pt-3 text-right font-display text-lg brand-orange">{formatINR(data.totals.gross_receipts)}</td>
                    </tr>
                  </tbody>
                </table>}
            </div>
            <div className="border border-white/12 bg-[#131A2B] p-5 print:border-neutral-300 print:bg-white">
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3 print:text-neutral-600">Receipts by category</div>
              <table className="w-full text-sm">
                <tbody>
                  <CatRow icon={Ticket}  label="Walk-in bookings" value={data.by_category.walk_in_bookings} count={data.counts.bookings} />
                  <CatRow icon={Receipt} label="Academy fees"     value={data.by_category.academy} />
                  <CatRow icon={Home}    label="Dormitory"        value={data.by_category.dormitory} />
                  {data.by_category.other_invoices > 0 && <CatRow label="Other invoices" value={data.by_category.other_invoices} />}
                </tbody>
              </table>
            </div>
          </div>

          {/* Cash reconciliation */}
          <div className="border border-brand-orange/40 bg-brand-orange/5 p-5 mb-6 print:border-neutral-300 print:bg-neutral-50">
            <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-3">Cash reconciliation · what should be in the till</div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-white/50 print:text-neutral-600 text-xs">Cash receipts</div>
                <div className="font-display text-2xl brand-blue print:text-black" data-testid="cash-receipts">+ {formatINR(data.cash.cash_receipts)}</div>
              </div>
              <div>
                <div className="text-white/50 print:text-neutral-600 text-xs">Cash expenses</div>
                <div className="font-display text-2xl text-red-400 print:text-black" data-testid="cash-expenses">- {formatINR(data.cash.cash_expenses)}</div>
              </div>
              <div>
                <div className="text-white/50 print:text-neutral-600 text-xs">Net in hand</div>
                <div className="font-display text-3xl font-black brand-orange print:text-black" data-testid="net-cash">{formatINR(data.cash.net_cash_in_hand)}</div>
              </div>
            </div>
          </div>

          {/* Discounts audit — only shown when there are discounts today */}
          {data.totals.discount_total > 0 && (
            <div className="border border-brand-blue/40 bg-brand-blue/5 p-5 mb-6 print:border-neutral-300 print:bg-white" data-testid="discounts-card">
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono text-[10px] uppercase tracking-widest brand-blue">Discounts given today · who waived how much</div>
                <div className="font-display text-2xl font-black brand-blue" data-testid="discount-total">
                  − {formatINR(data.totals.discount_total)}
                </div>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="font-mono text-[10px] uppercase tracking-widest text-white/40 border-b border-white/12 print:text-neutral-600">
                    <th className="text-left py-2">Staff</th>
                    <th className="text-right py-2">Bookings</th>
                    <th className="text-right py-2">Amount waived</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(data.discounts_by_staff || {}).map(([name, row]) => (
                    <tr key={name} className="border-b border-white/5 last:border-0" data-testid={`discount-row-${name}`}>
                      <td className="py-2">{name}</td>
                      <td className="py-2 text-right font-mono text-white/60">{row.count}</td>
                      <td className="py-2 text-right font-display font-bold brand-blue">− {formatINR(row.amount)}</td>
                    </tr>
                  ))}
                  {Object.keys(data.discounts_by_staff || {}).length === 0 && (
                    <tr><td colSpan={3} className="py-3 text-center text-white/40 text-xs">Only anonymous / legacy discounts recorded — enable staff attribution by ensuring admins are logged in.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Detailed line-items */}
          <div className="border border-white/12 bg-[#131A2B] overflow-x-auto print:border-neutral-300 print:bg-white" data-testid="dayclose-lines">
            <table className="w-full text-sm">
              <thead>
                <tr className="font-mono text-xs uppercase tracking-widest text-white/40 border-b border-white/12 print:text-neutral-600 print:border-neutral-300">
                  <th className="text-left p-3">Time</th>
                  <th className="text-left p-3">Kind</th>
                  <th className="text-left p-3">Reference</th>
                  <th className="text-left p-3">Party</th>
                  <th className="text-left p-3">Description</th>
                  <th className="text-left p-3">Method</th>
                  <th className="text-right p-3">Discount</th>
                  <th className="text-left p-3">By</th>
                  <th className="text-right p-3">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.lines.map((ln, i) => (
                  <tr key={i} className="border-b border-white/5 last:border-0 print:border-neutral-200">
                    <td className="p-3 font-mono text-[11px] text-white/50 print:text-black">{(ln.time || "").slice(11, 16) || "—"}</td>
                    <td className="p-3">
                      <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 ${
                        ln.kind === "expense" ? "bg-red-500/20 text-red-400"
                          : ln.kind === "invoice" ? "bg-brand-blue/20 text-brand-blue"
                          : "bg-brand-orange/20 text-brand-orange"
                      }`}>{ln.kind}</span>
                    </td>
                    <td className="p-3 font-mono text-xs">{ln.reference}</td>
                    <td className="p-3">{ln.party}</td>
                    <td className="p-3 text-xs text-white/70 print:text-black">{ln.description}</td>
                    <td className="p-3 font-mono text-xs uppercase tracking-widest">{METHOD_LABEL[ln.method] || ln.method}</td>
                    <td className="p-3 text-right font-mono text-xs text-white/60 print:text-black">
                      {ln.discount > 0 ? (
                        <span title={ln.discount_reason || ""} className="text-brand-blue">− {formatINR(ln.discount)}</span>
                      ) : "—"}
                    </td>
                    <td className="p-3 text-xs text-white/50 print:text-black">{ln.booked_by || "—"}</td>
                    <td className={`p-3 text-right font-mono ${ln.amount < 0 ? "text-red-400" : "brand-blue"} print:text-black`}>
                      {ln.amount < 0 ? "-" : ""}{formatINR(Math.abs(ln.amount))}
                    </td>
                  </tr>
                ))}
                {data.lines.length === 0 && (
                  <tr><td colSpan={9} className="p-6 text-center text-white/40">No transactions today.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Signature block for the physical printout */}
          <div className="hidden print:grid grid-cols-2 gap-16 mt-16 text-sm">
            <div>
              <div className="border-t border-black pt-2 w-64">Front-desk signature</div>
              <div className="mt-6 text-xs text-neutral-600">Date: {date}</div>
            </div>
            <div>
              <div className="border-t border-black pt-2 w-64">Admin signature</div>
              <div className="mt-6 text-xs text-neutral-600">HTP Sports Hub · Registered NGO</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone, testid, bold }) {
  const cls = tone === "blue" ? "brand-blue" :
              tone === "orange" ? "brand-orange" :
              tone === "lime" ? "text-emerald-400" :
              tone === "red" ? "text-red-400" : "text-white";
  return (
    <div data-testid={testid} className={`border ${bold ? "border-brand-orange" : "border-white/12"} bg-[#131A2B] p-4 print:border-neutral-300 print:bg-white`}>
      <div className="flex items-center justify-between mb-2">
        <Icon size={14} className={cls} />
        <span className="font-mono text-[10px] uppercase tracking-widest text-white/40 print:text-neutral-600">{label}</span>
      </div>
      <div className={`font-display text-3xl font-black ${cls} print:text-black`}>{formatINR(value)}</div>
    </div>
  );
}

function CatRow({ icon: Icon, label, value, count }) {
  return (
    <tr className="border-b border-white/8 last:border-0 print:border-neutral-200">
      <td className="py-2 flex items-center gap-2 text-white/70 print:text-black">
        {Icon && <Icon size={12} className="brand-orange" />}
        {label}
        {count != null && <span className="text-[10px] font-mono text-white/40">({count})</span>}
      </td>
      <td className="py-2 text-right font-display font-bold brand-blue print:text-black">{formatINR(value)}</td>
    </tr>
  );
}
